import React, { useCallback, useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import { FiAlertCircle, FiAward, FiCreditCard, FiLayers, FiPackage, FiRefreshCw } from "react-icons/fi";
import { SecondaryButton } from "../organisation/OrgModal";
import { MiniPager, Pill, Skeleton } from "./subscriptionUi";
import { apiErrorMessage, fetchAdminOrgPayments, formatCents, formatDate, metricForAddon, metricLabel, runningPlanSpan } from "../../utils/subscriptionData";

const PAGE_SIZE = 5;
const DAY = 86400000;
// Rows share one height and each card keeps a three-row minimum, so loading, empty and loaded all take
// the same room. Plan history can be long, so it scrolls inside its card instead of stretching the modal.
const ROW = "min-h-[2.75rem]";
const BODY = "min-h-[8.25rem]";

const REASONS = {
  trial_expired: "Trial ended",
  canceled: "Cancelled",
  payment_failed: "Payment failed",
  expired: "Expired",
  upgraded: "Upgraded",
  downgraded: "Downgraded",
  replaced: "Replaced",
};

const PAYMENT_TYPES = { plan: "Plan", renewal: "Renewal", addon: "Add-on", invoice: "Invoice" };
const PAYMENT_ICONS = { plan: FiAward, renewal: FiRefreshCw, addon: FiPackage };
const STATUS_TONES = { paid: "green", failed: "amber", open: "blue" };

const describePayment = (payment) => {
  if (payment.type === "addon") {
    const metric = metricForAddon(payment.addon_type);
    return `${payment.quantity ?? 1} × ${metric ? metricLabel(metric) : payment.addon_type || "add-on"}`;
  }
  return payment.plan_name || PAYMENT_TYPES[payment.type] || "Payment";
};

// "Oct 29, 2026 to Nov 5, 2026 · 7 days". Same-day plans show a single date.
const spanLabel = (item) => {
  const start = formatDate(item.start_at);
  if (item.active) return runningPlanSpan(item);
  const end = formatDate(item.end_at);
  const days = Math.round((new Date(item.end_at).getTime() - new Date(item.start_at).getTime()) / DAY);
  if (start === end || !Number.isFinite(days) || days < 1) return start === end ? start : `${start} to ${end}`;
  return `${start} to ${end} · ${days} ${days === 1 ? "day" : "days"}`;
};

const Card = ({ title, count, children, footer }) => {
  const theme = useTheme();
  return (
    <section className="flex min-w-0 flex-col rounded-xl border bg-white" style={{ borderColor: theme.palette.secondary.v4 }}>
      <header className="flex items-center justify-between gap-2 rounded-xl border-b bg-[#f3f6fb] px-3 py-2" style={{ borderColor: theme.palette.secondary.v4 }}>
        <h3 className="text-sm font-semibold">{title}</h3>
        {count > 0 && <span className="rounded-full bg-black/10 px-1.5 text-[11px] leading-4">{count}</span>}
      </header>
      {children}
      {footer}
    </section>
  );
};

const Message = ({ icon: Icon, title, children, action }) => (
  <div className={`flex flex-col items-center justify-center gap-1 px-4 text-center ${BODY}`}>
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f3f6fb] text-accent-blue" aria-hidden="true">
      <Icon className="h-4 w-4" />
    </span>
    <p className="text-sm font-medium">{title}</p>
    {children && <p className="max-w-xs text-xs text-accent-gray-light">{children}</p>}
    {action}
  </div>
);

const SkeletonRows = () => (
  <ul role="status" aria-label="Loading" className="divide-y">
    {[0, 1, 2].map((row) => (
      <li key={row} className={`flex items-center gap-3 px-3 py-1 ${ROW}`}>
        <Skeleton className="h-7 w-7 rounded-lg" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-4 w-14" />
      </li>
    ))}
  </ul>
);

// Plans the organisation has had, newest first, as a timeline. Long histories scroll inside the card.
const PlanTimeline = ({ history }) => {
  const theme = useTheme();
  const rows = history || [];
  return (
    <Card title="Plan history" count={rows.length}>
      {rows.length === 0 ? (
        <Message icon={FiLayers} title="No earlier plans">Plan changes will be listed here.</Message>
      ) : (
        <ol className={`max-h-[16rem] overflow-y-auto px-3 py-3 ${BODY}`} tabIndex={0} aria-label="Plan history, scrollable">
          {rows.map((item, index) => {
            const last = index === rows.length - 1;
            return (
              <li key={item.id || `${item.plan_id}-${index}`} className="relative pb-3 pl-6 last:pb-0">
                {!last && <span className="absolute bottom-0 left-[6px] top-4 w-px" style={{ backgroundColor: theme.palette.secondary.v4 }} aria-hidden="true" />}
                <span
                  className={`absolute left-0 top-1 h-3.5 w-3.5 rounded-full border-2 ${item.active ? "border-accent-blue bg-accent-blue" : "border-gray-400 bg-white"}`}
                  aria-hidden="true"
                />
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="text-sm font-medium">{item.name || item.plan_id}</span>
                  {item.active ? <Pill tone="blue">Current</Pill> : item.ended_reason ? <Pill tone="gray">{REASONS[item.ended_reason] || item.ended_reason}</Pill> : null}
                </div>
                <p className="text-xs text-accent-gray-light">{spanLabel(item)}</p>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
};

// Payments, five per page. Paging keeps the current rows on screen (dimmed) until the next page lands.
const PaymentsCard = ({ orgId }) => {
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ rows: [], total: 0, loaded: false, fetching: true, error: "" });

  useEffect(() => {
    setPage(1);
    setState({ rows: [], total: 0, loaded: false, fetching: true, error: "" });
  }, [orgId]);

  const load = useCallback(() => {
    let cancelled = false;
    setState((previous) => ({ ...previous, fetching: true, error: "" }));
    fetchAdminOrgPayments(orgId, { page, limit: PAGE_SIZE })
      .then((body) => !cancelled && setState({ rows: body.payments || [], total: body.total || 0, loaded: true, fetching: false, error: "" }))
      .catch((err) => !cancelled && setState((previous) => ({ ...previous, loaded: true, fetching: false, error: apiErrorMessage(err, "Could not load payments.") })));
    return () => {
      cancelled = true;
    };
  }, [orgId, page]);

  useEffect(load, [load]);

  const pages = Math.max(1, Math.ceil(state.total / PAGE_SIZE));
  const first = state.total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const last = Math.min(page * PAGE_SIZE, state.total);

  let body;
  if (!state.loaded) body = <SkeletonRows />;
  else if (state.error && !state.rows.length) {
    body = (
      <Message
        icon={FiAlertCircle}
        title={state.error}
        action={
          <SecondaryButton onClick={load} sx={{ mt: 1 }}>
            Try again
          </SecondaryButton>
        }
      />
    );
  } else if (!state.rows.length) body = <Message icon={FiCreditCard} title="No payments">This organisation hasn't paid for anything yet.</Message>;
  else {
    body = (
      <ul className={`divide-y transition-opacity ${state.fetching ? "opacity-50" : ""}`} aria-busy={state.fetching}>
        {state.rows.map((payment) => {
          const Icon = PAYMENT_ICONS[payment.type] || FiCreditCard;
          const paid = payment.status === "paid";
          return (
            <li key={payment.id} className={`flex items-center gap-3 px-3 py-1 ${ROW}`}>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#f3f6fb] text-accent-blue" aria-hidden="true">
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{describePayment(payment)}</p>
                <p className="truncate text-xs text-accent-gray-light">
                  {PAYMENT_TYPES[payment.type] || payment.type} · {formatDate(payment.paid_at || payment.failed_at || payment.created_at)}
                </p>
              </div>
              <span className="shrink-0 text-sm font-semibold tabular-nums">{formatCents(paid ? payment.amount_paid_cents : payment.amount_due_cents, payment.currency)}</span>
              <Pill tone={STATUS_TONES[payment.status] || "gray"}>{payment.status}</Pill>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Card
      title="Payments"
      count={state.total}
      footer={
        pages > 1 && (
          <footer className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2 text-xs text-accent-gray-light">
            <span aria-live="polite">
              {first}–{last} of {state.total}
            </span>
            <MiniPager page={page} pages={pages} onChange={setPage} disabled={state.fetching} />
          </footer>
        )
      }
    >
      {body}
    </Card>
  );
};

// History tab of the organisation details: plan timeline and payments side by side on wide screens
const OrgHistoryPanel = ({ orgId, history }) => (
  <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
    <PlanTimeline history={history} />
    <PaymentsCard orgId={orgId} />
  </div>
);

export default OrgHistoryPanel;
